import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { calculateCompoundInterest, calculateCredit } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { TrendingUp, Coins, PiggyBank, Receipt, Percent, Home, Calendar, BadgeDollarSign } from 'lucide-react';

const AnimatedValue = ({ value, formatFn, className = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    const duration = 700;
    const startTime = Date.now();
    const startValue = displayValue;
    const endValue = value;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(startValue + (endValue - startValue) * eased);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);
  return <span className={className}>{formatFn(Math.round(displayValue))}</span>;
};

const ResultCard = ({ label, value, icon: Icon, iconColor, gradient, formatFn, small }) => (
  <div className={`p-4 rounded-xl border-2 ${gradient} transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md`}>
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs uppercase tracking-[0.12em] font-semibold">{label}</span>
      <div className={`p-1.5 rounded-lg ${iconColor}`}><Icon className="h-4 w-4" /></div>
    </div>
    <AnimatedValue value={value} formatFn={formatFn}
      className={`${small ? 'text-xl' : 'text-2xl'} font-black tracking-tight`} />
  </div>
);

export const ResultsDisplay = () => {
  const { inputs, investmentType, t, formatMoney, language, currency } = useApp();
  const isCredit = investmentType === INVESTMENT_TYPES.CREDIT;

  const results = useMemo(() => {
    if (isCredit) {
      return calculateCredit({
        loanAmount: inputs.loanAmount ?? 200000,
        annualInterestRate: inputs.creditRate ?? 5,
        termMonths: inputs.creditTermMonths ?? 300,
        monthlyFeeRate: inputs.monthlyFeeRate ?? 0.03,
        gracePeriodMonths: inputs.gracePeriodMonths ?? 0,
        repaymentType: inputs.repaymentType ?? 'annuity',
        extraMonthlyPayment: inputs.extraMonthlyPayment ?? 0,
      });
    }
    const taxConfig = getTaxConfig(investmentType);
    const isAccumLike = investmentType === INVESTMENT_TYPES.ACCUMULATION_PLAN ||
      investmentType === INVESTMENT_TYPES.CHILD_FUTURE;
    return calculateCompoundInterest({
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isAccumLike ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
      topUps: inputs.topUps || [],
      withdrawals: inputs.withdrawals || [],
    });
  }, [inputs, isCredit]);

  const isAccumLike = investmentType === INVESTMENT_TYPES.ACCUMULATION_PLAN ||
    investmentType === INVESTMENT_TYPES.CHILD_FUTURE;

  return (
    <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden" data-testid="results-display">
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10">
            <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          {t('results')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {isCredit ? (
          <div className="space-y-4">
            <div className="mb-6 p-6 rounded-2xl bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/40 dark:to-red-900/40 border-2 border-orange-200 dark:border-orange-800">
              <div className="text-xs uppercase tracking-[0.15em] text-orange-700 dark:text-orange-300 font-semibold mb-2">{t('monthlyPayment')}</div>
              <div className="text-4xl sm:text-5xl font-black tracking-tight text-orange-800 dark:text-orange-200">
                <AnimatedValue value={results.monthlyPayment} formatFn={formatMoney} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ResultCard label={t('totalPayment')} value={results.totalPayment}
                icon={BadgeDollarSign} formatFn={formatMoney}
                iconColor="bg-red-200 dark:bg-red-800 text-red-700" small
                gradient="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200" />
              <ResultCard label={t('totalInterestCredit')} value={results.totalInterest}
                icon={Percent} formatFn={formatMoney}
                iconColor="bg-amber-200 dark:bg-amber-800 text-amber-700" small
                gradient="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200" />
              {results.totalFees > 0 && (
                <ResultCard label={t('totalFees')} value={results.totalFees}
                  icon={Receipt} formatFn={formatMoney}
                  iconColor="bg-purple-200 dark:bg-purple-800 text-purple-700" small
                  gradient="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200" />
              )}
              <ResultCard label={language === 'ro' ? 'Durată efectivă' : 'Effective term'}
                value={results.actualTermMonths}
                icon={Calendar}
                formatFn={(v) => `${v} ${language === 'ro' ? 'luni' : 'months'}`}
                iconColor="bg-blue-200 dark:bg-blue-800 text-blue-700" small
                gradient="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200" />
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 p-6 rounded-2xl bg-gradient-to-br from-blue-100 to-emerald-100 dark:from-blue-900/40 dark:to-emerald-900/40 border-2 border-blue-200 dark:border-blue-800">
              <div className="text-xs uppercase tracking-[0.15em] text-blue-700 dark:text-blue-300 font-semibold mb-2">{t('finalValue')}</div>
              <div className="text-4xl sm:text-5xl font-black tracking-tight text-blue-800 dark:text-blue-200">
                <AnimatedValue value={results.finalValue} formatFn={formatMoney} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ResultCard label={t('totalContributions')} value={results.totalContributions}
                icon={PiggyBank} formatFn={formatMoney}
                iconColor="bg-blue-200 dark:bg-blue-800 text-blue-700"
                gradient="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200" />
              <ResultCard label={inputs.includeTaxes ? t('interestNet') : t('totalInterest')}
                value={inputs.includeTaxes ? results.totalInterestNet : results.totalInterestGross}
                icon={Coins} formatFn={formatMoney}
                iconColor="bg-emerald-200 dark:bg-emerald-800 text-emerald-700"
                gradient="border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200" />
              {inputs.includeTaxes && results.totalTaxPaid > 0 && (
                <ResultCard label={t('taxPaid')} value={results.totalTaxPaid}
                  icon={Receipt} formatFn={formatMoney}
                  iconColor="bg-amber-200 dark:bg-amber-800 text-amber-700"
                  gradient="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200" />
              )}
              {isAccumLike && results.totalAdminFees > 0 && (
                <ResultCard label={t('adminFeePaid')} value={results.totalAdminFees}
                  icon={Percent} formatFn={formatMoney}
                  iconColor="bg-purple-200 dark:bg-purple-800 text-purple-700"
                  gradient="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200" />
              )}
              {results.totalTopUps > 0 && (
                <ResultCard label={t('totalTopUps')} value={results.totalTopUps}
                  icon={TrendingUp} formatFn={formatMoney}
                  iconColor="bg-teal-200 dark:bg-teal-800 text-teal-700"
                  gradient="border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200" />
              )}
              {results.totalWithdrawals > 0 && (
                <ResultCard label={t('totalWithdrawals')} value={results.totalWithdrawals}
                  icon={TrendingUp} formatFn={formatMoney}
                  iconColor="bg-orange-200 dark:bg-orange-800 text-orange-700"
                  gradient="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200" />
              )}
            </div>
          </>
        )}
        <div className="mt-6 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
          {t('taxDisclaimer')}
        </div>
      </CardContent>
    </Card>
  );
};

export default ResultsDisplay;
